import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientesPage from './pages/ClientesPage';
import PrestamosPage from './pages/PrestamosPage';
import PagosPage from './pages/PagosPage';
import RutasPage from './pages/RutasPage';
import CajaPage from './pages/CajaPage';
import ReportesPage from './pages/ReportesPage';
import UsuariosPage from './pages/UsuariosPage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import EmpleadosPage from './pages/EmpleadosPage';
import NominaPage from './pages/NominaPage';
import ContabilidadPage from './pages/ContabilidadPage';
import SimuladorPage from './pages/SimuladorPage';
import CobradorDashboardPage from './pages/CobradorDashboardPage';
import EmpresaPage from './pages/EmpresaPage';
import SolicitudesPage from './pages/SolicitudesPage';
import AnalisisDashboardPage from './pages/AnalisisDashboardPage';
import PruebaGratisPage from './pages/PruebaGratisPage';
import TrialExpiredPage from './pages/TrialExpiredPage';
import SuperAdminPage from './pages/SuperAdminPage';
import './App.css';

const ProtectedRoute = ({ children, roles }) => {
    const { user, loading, hasRole, trialExpired } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!user && !trialExpired) {
        return <Navigate to="/prueba-gratis" replace />;
    }

    if (trialExpired) {
        return <TrialExpiredPage />;
    }

    // Superadmin redirect: if superadmin tries to access a non-superadmin page
    if (user?.rol === 'superadmin' && roles && !roles.includes('superadmin')) {
        return <Navigate to="/superadmin" replace />;
    }

    if (roles && !hasRole(roles)) {
        return <Navigate to="/" replace />;
    }

    return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/prueba-gratis"
                element={
                    <PublicRoute>
                        <PruebaGratisPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/"
                element={
                    <ProtectedRoute roles={['admin', 'supervisor', 'cajero', 'cobrador']}>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/mi-ruta"
                element={
                    <ProtectedRoute>
                        <CobradorDashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/clientes"
                element={
                    <ProtectedRoute>
                        <ClientesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/prestamos"
                element={
                    <ProtectedRoute>
                        <PrestamosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/pagos"
                element={
                    <ProtectedRoute>
                        <PagosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/rutas"
                element={
                    <ProtectedRoute roles={['admin', 'supervisor']}>
                        <RutasPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/caja"
                element={
                    <ProtectedRoute>
                        <CajaPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reportes"
                element={
                    <ProtectedRoute roles={['admin', 'supervisor']}>
                        <ReportesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/usuarios"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <UsuariosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/configuracion"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <ConfiguracionPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/empleados"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <EmpleadosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/nomina"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <NominaPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/contabilidad"
                element={
                    <ProtectedRoute roles={['admin', 'supervisor']}>
                        <ContabilidadPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/simulador"
                element={
                    <ProtectedRoute>
                        <SimuladorPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/empresa"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <EmpresaPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/solicitudes"
                element={
                    <ProtectedRoute>
                        <SolicitudesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/analisis"
                element={
                    <ProtectedRoute roles={['admin', 'supervisor']}>
                        <AnalisisDashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/superadmin"
                element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperAdminPage />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster 
                    position="top-right" 
                    richColors 
                    closeButton
                    toastOptions={{
                        style: {
                            fontFamily: 'IBM Plex Sans, sans-serif',
                        },
                    }}
                />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
